import { z } from 'zod';

// ---- Tag catalog (TROLL / OGRE + custom) ----

export const OrderFlagTagSchema = z.object({
    id: z.number(),
    code: z.string(),
    label: z.string(),
    description: z.string().nullish(),
    color: z.string().nullish(),
    // Seeded defaults (TROLL, OGRE). Code is frozen and they can't be deleted.
    system: z.boolean().optional().default(false),
    active: z.boolean(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
});

export type OrderFlagTag = z.infer<typeof OrderFlagTagSchema>;

export const OrderFlagTagListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(OrderFlagTagSchema),
    count: z.number().optional(),
});

export const OrderFlagTagResponseSchema = z.object({
    success: z.boolean(),
    data: OrderFlagTagSchema,
});

const codePattern = /^[a-z0-9][a-z0-9_-]{0,62}[a-z0-9]$|^[a-z0-9]$/;

export const OrderFlagTagFormSchema = z.object({
    code: z
        .string()
        .min(1, 'Codul este obligatoriu')
        .max(64, 'Codul are maxim 64 de caractere')
        .regex(
            codePattern,
            'Doar litere mici, cifre, "-" si "_". Nu poate incepe sau termina cu "-" sau "_".',
        ),
    label: z.string().min(1, 'Eticheta este obligatorie'),
    description: z.string().optional(),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'Culoarea trebuie sa fie un cod hex (#rrggbb).')
        .optional()
        .or(z.literal('')),
    active: z.boolean(),
});

export type OrderFlagTagForm = z.infer<typeof OrderFlagTagFormSchema>;

// ---- Blocklist entries ----

export const OrderFlagSchema = z.object({
    id: z.number(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
    tagId: z.number(),
    note: z.string().nullish(),
    createdBy: z.number().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    tag: OrderFlagTagSchema.optional(),
});

export type OrderFlag = z.infer<typeof OrderFlagSchema>;

export const OrderFlagListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(OrderFlagSchema),
    count: z.number(),
});

export const OrderFlagResponseSchema = z.object({
    success: z.boolean(),
    data: OrderFlagSchema,
});

export const OrderFlagFormSchema = z
    .object({
        email: z.string().email('Email invalid.').optional().or(z.literal('')),
        phone: z.string().optional().or(z.literal('')),
        tagId: z.coerce.number().int().positive('Alege o eticheta.'),
        note: z.string().optional(),
    })
    // An entry must carry at least one contact key (email or phone) — that's
    // what the order is matched against.
    .refine((v) => !!(v.email && v.email.trim()) || !!(v.phone && v.phone.trim()), {
        message: 'Completeaza cel putin email sau telefon.',
        path: ['email'],
    });

export type OrderFlagForm = z.infer<typeof OrderFlagFormSchema>;

// ---- Compact active set the order badge matches against ----

export const ActiveOrderFlagSchema = z.object({
    id: z.number(),
    emailNormalized: z.string().nullish(),
    phoneNormalized: z.string().nullish(),
    tag: z.object({
        code: z.string(),
        label: z.string(),
        color: z.string().nullish(),
        description: z.string().nullish(),
    }),
});

export type ActiveOrderFlag = z.infer<typeof ActiveOrderFlagSchema>;

export const ActiveOrderFlagListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(ActiveOrderFlagSchema),
    count: z.number().optional(),
});
